import axios from 'axios';
import { API_URL } from '../config';

// Create a mock API module for development
// This is a completely separate implementation used only in development mode
const createMockApi = () => {
  const mockMessages = [];
  // Generate phone numbers for conversations
  const phoneNumbers = [];
  for (let i = 1; i <= 5; i++) {
    phoneNumbers.push(`+1800${Math.floor(Math.random() * 9000000) + 1000000}`);
  }
  
  // Generate some mock messages
  for (let i = 1; i <= 30; i++) {
    // Assign to one of our phone numbers
    const toNumber = phoneNumbers[Math.floor(Math.random() * phoneNumbers.length)];
    
    // New messages will be more recent (within the last 5 minutes)
    const isNew = i <= 5;
    const messageTime = isNew 
      ? new Date(Date.now() - Math.floor(Math.random() * 300000)) // Within last 5 minutes
      : new Date(Date.now() - (Math.floor(Math.random() * 3600000) + 600000)); // Older than 10 minutes
    
    mockMessages.push({
      _id: `msg-${i}`,
      to: toNumber,
      from: `+1${Math.floor(Math.random() * 9000000) + 1000000}`,
      text: `This is a sample message ${i}. This is a test message for development mode.`,
      date: messageTime.toISOString(),
      status: 1,
      vCode: i % 4 === 0 ? '123456' : null,
      app: ['Amazon', 'Google', 'Facebook', 'Apple', 'Microsoft'][Math.floor(Math.random() * 5)]
    });
  }

  return {
    get: (url) => {
      console.log('MOCK API GET:', url);
      
      // Auth endpoints
      if (url.includes('/api/v1/auth/me')) {
        return Promise.resolve({
          data: {
            id: 'dev-user-id',
            email: 'dev@example.com',
            role: 'admin',
            balance: 1000,
            username: 'dev',
            created_at: new Date().toISOString()
          }
        });
      }
      
      // Account endpoints
      if (url.includes('/api/v1/users/accounts') || url.includes('/api/v1/user/accounts')) {
        return Promise.resolve({
          data: {
            accounts: [
              {
                account_id: 'dev-account-1',
                platform: 'wephone',
                phone: '+12345678901',
                email: 'dev@example.com',
                status: 'active',
                created_at: new Date().toISOString(),
                updated_at: new Date().toISOString(),
                accountId: 'platform-account-1'
              }
            ],
            total: 1,
            page: 1,
            per_page: 10
          }
        });
      }
      
      // SMS endpoints
      if (url.includes('/api/v1/sms/')) {
        // Check if we should simulate a 402 error
        const currentUrl = window.location.href;
        const has402Flag = window.location.search.includes('402') || 
                          currentUrl.includes('payment=402');
        
        console.log('Checking for 402 flag:', { 
          currentUrl, 
          windowSearch: window.location.search,
          has402Flag 
        });
                          
        // Test insufficient balance scenario - 402 Payment Required
        if (has402Flag) {
          console.log('MOCK API: Simulating 402 Payment Required error');
          const error = new Error('您的账户余额不足，无法获取消息。请联系管理员添加余额。');
          error.response = {
            status: 402,
            data: {
              error: '余额不足',
              message: '您的账户余额不足，无法获取消息。请联系管理员添加余额。'
            }
          };
          error.isPaymentError = true;  // Match our custom error structure
          return Promise.reject(error);
        }
        
        // Test insufficient balance scenario with response code
        if (url.includes('insufficient=true')) {
          return Promise.resolve({
            data: {
              retcode: 1001,
              retmsg: '余额不足，无法获取消息',
              result: {
                rows: []
              }
            }
          });
        }

        return Promise.resolve({
          data: {
            result: {
              rows: mockMessages
            }
          }
        });
      }
      
      // Default response for unhandled GET requests
      return Promise.resolve({ data: {} });
    },
    
    post: (url, data) => {
      console.log('MOCK API POST:', url, data);
      
      // Login endpoint
      if (url.includes('/api/v1/auth/login')) {
        return Promise.resolve({
          data: {
            user: {
              id: 'dev-user-id',
              email: 'dev@example.com',
              role: 'admin',
              balance: 1000,
              username: 'dev',
              created_at: new Date().toISOString()
            },
            token: 'dev-token-' + Date.now(),
            refreshToken: 'dev-refresh-token-' + Date.now(),
            expiresIn: 3600
          }
        });
      }
      
      // Bulk subscribe endpoint
      if (url.includes('/bulk-subscribe') || url.includes('/subscribe')) {
        return Promise.resolve({
          data: {
            message: 'Bulk subscription completed',
            stats: {
              already_subscribed: 3,
              newly_subscribed: 2,
              failed: 0,
              total: 5
            },
            results: {}
          }
        });
      }
      
      // Default response for unhandled POST requests
      return Promise.resolve({ data: {} });
    },
    
    put: (url, data) => {
      console.log('MOCK API PUT:', url, data);
      return Promise.resolve({ data: { success: true } });
    },
    
    delete: (url) => {
      console.log('MOCK API DELETE:', url);
      return Promise.resolve({ data: { success: true } });
    }
  };
};

// Create axios instance OR mock API based on environment
const useDevMockApi = process.env.NODE_ENV === 'development' && 
  (localStorage.getItem('token') === 'dev-token-for-testing' || 
   window.location.pathname.includes('/login')); // Always use mock API on login page in dev

const apiClient = useDevMockApi ? createMockApi() : axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Only add interceptors when using real axios instance
if (!useDevMockApi) {
  // Add auth token to requests
  apiClient.interceptors.request.use((config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  }, (error) => {
    return Promise.reject(error);
  });

  // Add response interceptor to handle DEV MODE responses
  apiClient.interceptors.response.use(
    (response) => {
      return response;
    },
    (error) => {
      // Dev mode mock data - always active in development mode
      if (process.env.NODE_ENV === 'development') {
        console.log('DEV MODE: Intercepting API error and providing mock data');
        console.log('Failed endpoint:', error.config.url, 'Method:', error.config.method);
        
        // Check which endpoint is being called and return appropriate mock data
        const url = error.config.url;
        
        // Auth endpoints
        if (url.includes('/api/v1/auth/me')) {
          return Promise.resolve({
            data: {
              id: 'dev-user-id',
              email: 'dev@example.com',
              role: 'admin',
              balance: 1000,
              username: 'dev',
              created_at: new Date().toISOString()
            }
          });
        }
        
        // Account endpoints
        if (url.includes('/api/v1/users/accounts') || url.includes('/api/v1/user/accounts')) {
          return Promise.resolve({
            data: {
              accounts: [
                {
                  account_id: 'dev-account-1',
                  platform: 'wephone',
                  phone: '+12345678901',
                  email: 'dev@example.com',
                  status: 'active',
                  created_at: new Date().toISOString(),
                  updated_at: new Date().toISOString(),
                  accountId: 'platform-account-1'
                }
              ],
              total: 1,
              page: 1,
              per_page: 10
            }
          });
        }
        
        // Admin users with stats endpoint
        if (url.includes('/api/v1/admin/users/stats')) {
          console.log('DEV MODE: Providing mock users with stats data');
          return Promise.resolve({
            data: {
              users: [
                {
                  uid: 'user1',
                  email: 'user1@example.com',
                  role: 'admin',
                  status: 'active',
                  balance: 250.75,
                  rate: 1.0,
                  account_count: 3,
                  subscribed_numbers_count: 12
                },
                {
                  uid: 'user2',
                  email: 'user2@example.com',
                  role: 'user',
                  status: 'active',
                  balance: 50.25,
                  rate: 0.75,
                  account_count: 1,
                  subscribed_numbers_count: 5
                },
                {
                  uid: 'user3',
                  email: 'user3@example.com',
                  role: 'user',
                  status: 'disabled',
                  balance: 0,
                  rate: 1.0,
                  account_count: 0,
                  subscribed_numbers_count: 0
                }
              ],
              total: 3,
              page: 1,
              per_page: 10
            }
          });
        }
        
        // Admin user stats endpoint
        if (url.includes('/api/v1/admin/users/') && url.includes('/stats')) {
          console.log('DEV MODE: Providing mock user stats data');
          const userId = url.split('/').pop().replace('/stats', '');
          
          // Mock data for different users
          const mockData = {
            user1: {
              account_count: 3,
              subscribed_numbers_count: 12
            },
            user2: {
              account_count: 1,
              subscribed_numbers_count: 5
            },
            user3: {
              account_count: 0,
              subscribed_numbers_count: 0
            }
          };
          
          return Promise.resolve({
            data: mockData[userId] || { account_count: 0, subscribed_numbers_count: 0 }
          });
        }
        
        // SMS endpoints - support both /sms/wephone and /sms/platform URL patterns
        if (url.includes('/api/v1/sms/')) {
          const mockMessages = [];
          
          // Generate random phone numbers for conversations
          const phoneNumbers = [];
          for (let i = 1; i <= 5; i++) {
            phoneNumbers.push(`+1800${Math.floor(Math.random() * 9000000) + 1000000}`);
          }
          
          // Generate some mock messages
          for (let i = 1; i <= 30; i++) {
            // Assign to one of our phone numbers
            const toNumber = phoneNumbers[Math.floor(Math.random() * phoneNumbers.length)];
            
            // New messages will be more recent (within the last 5 minutes)
            const isNew = i <= 5;
            const messageTime = isNew 
              ? new Date(Date.now() - Math.floor(Math.random() * 300000)) // Within last 5 minutes
              : new Date(Date.now() - (Math.floor(Math.random() * 3600000) + 600000)); // Older than 10 minutes
            
            mockMessages.push({
              _id: `msg-${i}`,
              to: toNumber,
              from: `+1${Math.floor(Math.random() * 9000000) + 1000000}`,
              text: `This is a sample message ${i}. This is a test message for development mode.`,
              date: messageTime.toISOString(),
              status: 1,
              vCode: i % 4 === 0 ? '123456' : null,
              app: ['Amazon', 'Google', 'Facebook', 'Apple', 'Microsoft'][Math.floor(Math.random() * 5)]
            });
          }
          
          return Promise.resolve({
            data: {
              result: {
                rows: mockMessages
              }
            }
          });
        }

        // Bulk subscribe endpoint
        if ((url.includes('/bulk-subscribe') || url.includes('/subscribe')) && error.config.method === 'post') {
          return Promise.resolve({
            data: {
              message: 'Bulk subscription completed',
              stats: {
                already_subscribed: 3,
                newly_subscribed: 2,
                failed: 0,
                total: 5
              },
              results: {}
            }
          });
        }
      }
      
      return Promise.reject(error);
    }
  );
}

// Auth API calls
export const authApi = {
  // Login with username
  login: async (username, password) => {
    try {
      const response = await apiClient.post('/api/v1/auth/login', { username, password });
      if (response.data.token) {
        localStorage.setItem('token', response.data.token);
        // Store refresh token
        localStorage.setItem('refreshToken', response.data.refreshToken);
        // Store token expiration time
        const expiresIn = parseInt(response.data.expiresIn) || 3600;
        const expirationTime = new Date().getTime() + expiresIn * 1000;
        localStorage.setItem('tokenExpiration', expirationTime);
      }
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Logout
  logout: async () => {
    try {
      const response = await apiClient.post('/api/v1/auth/logout');
      localStorage.removeItem('token');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('tokenExpiration');
      return response.data;
    } catch (error) {
      localStorage.removeItem('token');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('tokenExpiration');
      throw error;
    }
  },

  // Get current user
  getCurrentUser: async () => {
    try {
      const response = await apiClient.get('/api/v1/auth/me');
      return response.data;
    } catch (error) {
      throw error;
    }
  },
  
  // Refresh token
  refreshToken: async () => {
    try {
      const refreshToken = localStorage.getItem('refreshToken');
      if (!refreshToken) {
        throw new Error('No refresh token available');
      }
      
      const response = await apiClient.post('/api/v1/auth/refresh-token', { refresh_token: refreshToken });
      
      if (response.data.token) {
        localStorage.setItem('token', response.data.token);
        localStorage.setItem('refreshToken', response.data.refreshToken);
        
        // Update token expiration time
        const expiresIn = parseInt(response.data.expiresIn) || 3600;
        const expirationTime = new Date().getTime() + expiresIn * 1000;
        localStorage.setItem('tokenExpiration', expirationTime);
      }
      
      return response.data;
    } catch (error) {
      throw error;
    }
  },
};

// User API calls
export const userApi = {
  // Get user profile
  getProfile: async () => {
    try {
      const response = await apiClient.get('/api/v1/users/profile');
      return response.data;
    } catch (error) {
      throw error;
    }
  },
  
  // Get transaction history
  getTransactions: async (page = 1, perPage = 10) => {
    try {
      const response = await apiClient.get('/api/v1/users/transactions', {
        params: { page, per_page: perPage }
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },
  
  // Create a new account
  createAccount: async (accountData) => {
    try {
      const response = await apiClient.post('/api/v1/users/account', accountData);
      return response.data;
    } catch (error) {
      throw error;
    }
  },
  
  // Get user accounts
  getAccounts: async (page = 1, perPage = 10) => {
    try {
      const response = await apiClient.get('/api/v1/users/accounts', {
        params: { page, per_page: perPage }
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },
  
  // Delete an account
  deleteAccount: async (accountId) => {
    try {
      const response = await apiClient.delete(`/api/v1/users/account/${accountId}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },
  
  // Subscribe a phone number to an account
  subscribeNumber: async (accountId, phoneNumber) => {
    try {
      const response = await apiClient.post(`/api/v1/users/account/${accountId}/subscribe`, {
        phone_number: phoneNumber
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },
  
  // Bulk subscribe multiple phone numbers to an account
  bulkSubscribeNumbers: async (accountId, phoneNumbers) => {
    try {
      const response = await apiClient.post(`/api/v1/users/account/${accountId}/bulk-subscribe`, {
        phone_numbers: phoneNumbers
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },
  
  // Unsubscribe a phone number from an account
  unsubscribeNumber: async (accountId, phoneNumber) => {
    try {
      const response = await apiClient.delete(`/api/v1/users/account/${accountId}/subscribe/${phoneNumber}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }
};

// Admin API calls
export const adminApi = {
  // Get all users (admin only)
  getUsers: async (page = 1, perPage = 10) => {
    try {
      const response = await apiClient.get('/api/v1/admin/users', {
        params: { page, per_page: perPage }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching users:', error);
      throw error;
    }
  },

  // Get all users with stats (admin only)
  getUsersWithStats: async (page = 1, perPage = 10) => {
    try {
      const response = await apiClient.get('/api/v1/admin/users/stats', {
        params: { page, per_page: perPage }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching users with stats:', error);
      throw error;
    }
  },

  // Get stats for a specific user (admin only)
  getUserStats: async (userId) => {
    try {
      const response = await apiClient.get(`/api/v1/admin/users/${userId}/stats`);
      return response.data;
    } catch (error) {
      console.error('Error fetching user stats:', error);
      throw error;
    }
  },

  // Create user (admin only)
  createUser: async (userData) => {
    try {
      const response = await apiClient.post('/api/v1/admin/users', userData);
      console.log('Create user response:', response);
      return response.data;
    } catch (error) {
      console.error('Error creating user:', error);
      throw error;
    }
  },

  // Update user (admin only)
  updateUser: async (userId, userData) => {
    try {
      const response = await apiClient.put(`/api/v1/admin/users/${userId}`, userData);
      return response.data;
    } catch (error) {
      console.error('Error updating user:', error);
      throw error;
    }
  },

  // Delete user (admin only)
  deleteUser: async (userId) => {
    try {
      const response = await apiClient.delete(`/api/v1/admin/users/${userId}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting user:', error);
      throw error;
    }
  },

  // Update user status (admin only)
  toggleUserStatus: async (userId, status) => {
    try {
      const response = await apiClient.put(`/api/v1/admin/users/${userId}/status`, { status });
      return response.data;
    } catch (error) {
      console.error('Error updating user status:', error);
      throw error;
    }
  }
};

// SMS API calls
export const smsApi = {
  // Get SMS messages for an account
  getMessages: async (accountId, page = 1, perPage = 10) => {
    try {
      const response = await apiClient.get(`/api/v1/sms/${accountId}/messages`, {
        params: { page, per_page: perPage }
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },
  
  // Get single SMS message
  getMessage: async (messageId) => {
    try {
      const response = await apiClient.get(`/api/v1/sms/message/${messageId}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },
  
  // Get SMS messages for an account from a specific platform
  getAccountMessages: async (account, platform, requestNumber = null) => {
    try {
      console.log(`Making API request to /api/v1/sms/${platform}`);
      
      // Extract the platform account ID from the account object
      const platformAccountId = account.accountId;
      const firebaseAccountId = account.account_id;
      
      if (!platformAccountId) {
        console.error('No platform account ID found in account object:', account);
        throw new Error('Platform account ID not found');
      }
      
      console.log(`Using Firebase account ID: ${firebaseAccountId}`);
      console.log(`Using platform account ID: ${platformAccountId}`);
      
      // Send the platform account ID as the parameter, not the entire account object
      const params = { 
        account_id: firebaseAccountId,
        platform_account_id: platformAccountId,
        rows: 20  // Limit to 20 messages
      };
      
      // Add request_number parameter if provided
      if (requestNumber) {
        params.request_number = requestNumber;
        console.log(`Filtering by request number: ${requestNumber}`);
      }
      
      try {
        const response = await apiClient.get(`/api/v1/sms/${platform}`, { params });
        console.log('API response status:', response.status);
        console.log('API response data:', response.data);
        return response.data;
      } catch (error) {
        // Re-throw with clear status for better error handling
        if (error.response) {
          if (error.response.status === 402) {
            console.error('HTTP 402: Payment Required - Insufficient balance');
            const customError = new Error('您的账户余额不足，无法获取消息。请联系管理员添加余额。');
            customError.response = error.response;
            customError.isPaymentError = true;
            throw customError;
          }
        }
        throw error;
      }
    } catch (error) {
      console.error('Error fetching account messages:', error);
      
      // Log more details about the error
      if (error.response) {
        console.error('Error response status:', error.response.status);
        console.error('Error response data:', error.response.data);
      }
      
      // Preserve any isPaymentError flag we might have set
      throw error;
    }
  }
};